import {
  API_VERSION_DEFAULTS,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import type { ApiResponse } from '~/common/models/apis/apiResponse.model';
import type { NonNullablePick } from '~/common/models/common.model';
import type {
  ApiConnectivityStatusSummary,
  ChargeLevelSummary,
  ConnectivityStatusSummary,
  DiagnosticSummary,
  OilLifeSummary,
  RecallSummary,
} from '~/common/models/csDashboard.model';
import { BDError } from '~/common/models/error.model';
import { NOT_EQUIPPED_FLAG } from '~/common/models/featureFlags.model';
import {
  addOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';
import { mapConnectivityStatusSummary } from '../connectivity/connectivity.mappers';

export type CSDashboardSummaryParams = NonNullablePick<
  RouteParams,
  'organizationsId'
> &
  Pick<RouteParams, 'hubsId' | 'fleetsId'> & {
    sessionId: string;
  };

export enum CSDashboardActionType {
  GET_CONNECTIVITY_STATUS_SUMMARY = 'dashboard/getConnectivityStatusSummary',
}

const getConnectivityStatusSummary = makeThunk(
  CSDashboardActionType.GET_CONNECTIVITY_STATUS_SUMMARY,
  makeGetPayloadCreator<
    ApiResponse<ConnectivityStatusSummary>,
    CSDashboardSummaryParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.connectivityStatusAPI}/views/connectivityStatusDashboardSummary`,
    axiosOptions: ({ organizationsId }, _state) =>
      addOrganizationIdHeader(DEFAULT_API_CONFIG, organizationsId),
    argAdapter: ({ fleetsId }) => {
      return {
        requestParams: {
          fleetId: fleetsId,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (response && hasApiResult<ApiConnectivityStatusSummary>(response)) {
        const isNotEquippedFeatureFlag = Boolean(
          state.featureFlags.entities[NOT_EQUIPPED_FLAG]?.value
        );
        return {
          ...response,
          result: mapConnectivityStatusSummary(
            response.result,
            isNotEquippedFeatureFlag
          ),
        };
      }
      throw new BDError('Unexpected Connectivity Status Summary response', {
        data: response,
      });
    },
  })
);

export const getDiagnosticsSummary = makeThunk(
  'csDashboard/fetchDiagnosticsSummary',
  makeGetPayloadCreator<
    ApiResponse<DiagnosticSummary>,
    CSDashboardSummaryParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.connectivityStatusAPI}/views/diagnosticDashboardSummary`,
    axiosOptions: ({ organizationsId }) =>
      addOrganizationIdHeader(DEFAULT_API_CONFIG, organizationsId),
    argAdapter: ({ fleetsId, hubsId }) => {
      return {
        requestParams: {
          hubId: hubsId,
          fleetId: fleetsId,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<DiagnosticSummary>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected diagnostic summary response', {
        data: response,
      });
    },
  })
);

export const postRecallSummary = makeThunk(
  'csDashboard/postRecallSummary',
  makePostPayloadCreator<ApiResponse<RecallSummary>, CSDashboardSummaryParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.recallSummary}/fieldactions/vehicleSummary`,
    axiosOptions: ({ organizationsId }, _state) =>
      addOrganizationIdHeader(DEFAULT_API_CONFIG, organizationsId),
    argAdapter: ({ fleetsId }) => {
      return {
        requestBody: {
          fleetId: fleetsId,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<RecallSummary>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected diagnostic summary response', {
        data: response,
      });
    },
  })
);

export const getOilLifeSummary = makeThunk(
  'csDashboard/getOilLifeSummary',
  makeGetPayloadCreator<ApiResponse<OilLifeSummary>, CSDashboardSummaryParams>({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.connectivityStatusAPI}/views/oilLifeDashboardSummary`,

    axiosOptions: ({ organizationsId }) =>
      addOrganizationIdHeader(DEFAULT_API_CONFIG, organizationsId),
    argAdapter: ({ fleetsId, hubsId }) => {
      return {
        requestParams: {
          fleetId: fleetsId,
          hubId: hubsId,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<OilLifeSummary>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected Oil Life summary response', {
        data: response,
      });
    },
  })
);

export const CS_DASHBOARD_ACTIONS = {
  getConnectivityStatusSummary,
  getDiagnosticsSummary,
  postRecallSummary,
  getOilLifeSummary,
};
